import { useCallback } from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import to from 'await-to-js';

import { notify } from '@/components/toast';
import { ARCHIVE_TRACE_STATES_MUTATION } from '@/gql/queries/archive-trace-states.gql';

type UseArchiveTraceStatesOptions = {
  onSuccess?: () => void;
};

export function useArchiveTraceStates(options?: UseArchiveTraceStatesOptions) {
  const [archiveTraceStatesMutation] = useMutation(
    ARCHIVE_TRACE_STATES_MUTATION
  );

  const archiveTraceStates = useCallback(
    async (traceIds: string[], archived: boolean) => {
      if (!traceIds.length) return;

      const promise = archiveTraceStatesMutation({
        variables: {
          traceIds,
          archived,
          manuallyEdited: true
        }
      });

      notify.promise(promise, {
        loading: archived ? 'Archiving traces...' : 'Unarchiving traces...',
        success: archived
          ? 'Traces have been archived'
          : 'Traces have been unarchived',
        error: error => {
          Sentry.captureException(error, {
            tags: { component: 'useArchiveTraceStates' },
            extra: { traceIds, archived }
          });
          return 'Something went wrong during the archive operation...';
        }
      });

      const [err] = await to(promise);
      if (!err) {
        options?.onSuccess?.();
      }
    },
    [archiveTraceStatesMutation, options?.onSuccess]
  );

  return archiveTraceStates;
}
